import { useState } from 'react'
import { Bandeau, Champ, Liste, Zone } from './Champs'
import { BlocReputation } from './BlocReputation'
import { libelleVille, useVille } from '../VilleContexte'
import { Bloc, Bouton, Icone, LienBouton, Puce } from '../ui'
import {
  ErreurApi,
  activerRole,
  deconnecter,
  deconnecterPartout,
  demanderReinitialisation,
  desactiverRole,
  mettreAJourProfil,
  profil as chargerProfil,
  type Genre,
  type Profil as ProfilDto,
  type Role,
} from '../api'
import { moisAnnee, nombre } from '../formats'
import { LANGUES, NOMS_DE_LANGUE, TEXTES, messageErreur, type Langue } from '../i18n'

interface Props {
  langue: Langue
  profil: ProfilDto
  onRafraichir: (profil: ProfilDto) => void
  onDeconnecte: () => void
}

/** Rôles auto-activables (conception L1, §1.2). Les deux autres sont affichés, jamais cliquables. */
const ROLES_AUTO: readonly Role[] = ['Seeker', 'Owner']

/** Le rang d'un niveau, pour la jauge : N0 vide, N4 pleine. */
const RANGS: Record<ProfilDto['trustLevel'], number> = { N0: 0, N1: 1, N2: 2, N3: 3, N4: 4 }

/** Les initiales du nom affiché — deux lettres au plus, dans n'importe quel alphabet. */
function initiales(nom: string): string {
  return nom
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot.charAt(0))
    .join('')
    .toLocaleUpperCase()
}

/**
 * Mon profil — L1, revu le 13/09/2026 (maquette `docs/ui/pwa/profil.html`).
 *
 * **Deux colonnes : ce que je déclare, ce que la plateforme constate.** À gauche l'identité et
 * les rôles, que la personne modifie ; à droite le niveau de confiance, la sécurité et la
 * réputation, qu'elle ne modifie pas. Un rôle qui découle d'un fait ne s'active pas à la main :
 * « Locataire » vient d'un bail signé, « Agence » d'une décision d'administrateur, et aucune
 * promotion vers un rôle d'administration n'existe depuis cette API (invariant 4).
 *
 * **Le niveau de confiance est expliqué critère par critère**, tels que le domaine les calcule
 * (`User.ComputeLevel`) — et non tels que la spec les prévoyait : la vérification du contact est
 * différée (D22), et la photo de profil n'existe pas encore. Un critère inatteignable se dit
 * comme tel plutôt que de rester grisé sans raison.
 */
export function Profil({ langue, profil, onRafraichir, onDeconnecte }: Props) {
  const t = TEXTES[langue]

  const [nomAffiche, setNomAffiche] = useState(profil.displayName)
  const [prenom, setPrenom] = useState(profil.firstName ?? '')
  const [nom, setNom] = useState(profil.lastName ?? '')
  const [presentation, setPresentation] = useState(profil.bio ?? '')
  const [dateNaissance, setDateNaissance] = useState(profil.birthDate ?? '')
  const [genre, setGenre] = useState<Genre>(profil.gender ?? 'Unspecified')
  const [langueProfil, setLangueProfil] = useState(profil.preferredLanguage)
  // « Ma ville » (L13, D45) : la ville sur laquelle le site et l'application s'ouvrent. Vide =
  // jamais choisie ; la chaîne de résolution fait alors le reste.
  const [villeProfil, setVilleProfil] = useState(profil.preferredCityCode ?? '')
  const ville = useVille()

  const [erreur, setErreur] = useState<string | null>(null)
  const [succes, setSucces] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const [envoiEnCours, setEnvoiEnCours] = useState(false)

  const rang = RANGS[profil.trustLevel]
  const libelleNiveau: Record<ProfilDto['trustLevel'], string> = {
    N0: t.profilConfianceN0,
    N1: t.profilConfianceN1,
    N2: t.profilConfianceN2,
    N3: t.profilConfianceN3,
    N4: t.profilConfianceN4,
  }
  const contactVerifie = profil.emailConfirmed || profil.phoneVerified
  const contact = profil.email ?? profil.phoneNumber

  function signaler(echec: unknown) {
    setErreur(messageErreur(langue, echec instanceof ErreurApi ? echec.code : 'internal'))
  }

  async function enregistrer(evenement: React.FormEvent) {
    evenement.preventDefault()
    setErreur(null)
    setSucces(null)
    setEnCours(true)

    try {
      await mettreAJourProfil({
        displayName: nomAffiche,
        firstName: prenom,
        lastName: nom,
        bio: presentation,
        // Une chaîne vide vaut « inchangé » côté API : envoyer `null` évite d'effacer une date
        // déjà posée par un simple passage sur le formulaire.
        birthDate: dateNaissance === '' ? null : dateNaissance,
        gender: genre,
        preferredLanguage: langueProfil,
        preferredCityCode: villeProfil === '' ? null : villeProfil,
      })

      // La ville courante suit le compte : sans cela, l'en-tête garderait l'ancienne jusqu'au
      // prochain chargement, et « enregistré » aurait l'air de n'avoir rien fait.
      if (villeProfil !== '') {
        ville.choisir(villeProfil)
      }

      onRafraichir(await chargerProfil())
      setSucces(t.enregistre)
    } catch (echec) {
      signaler(echec)
    } finally {
      setEnCours(false)
    }
  }

  async function basculerRole(role: Role, actif: boolean) {
    setErreur(null)
    setSucces(null)

    try {
      await (actif ? desactiverRole(role) : activerRole(role))
      onRafraichir(await chargerProfil())
    } catch (echec) {
      signaler(echec)
    }
  }

  /*
    Pas d'écran « ancien mot de passe / nouveau mot de passe » : le changement passe par le même
    lien que l'oubli, envoyé au contact du compte. Un seul chemin pour poser un mot de passe, donc
    une seule règle de robustesse et un seul endroit où la session se renouvelle.
  */
  async function changerMotDePasse() {
    if (contact === null) {
      return
    }

    setErreur(null)
    setSucces(null)
    setEnvoiEnCours(true)

    try {
      await demanderReinitialisation(contact)
      setSucces(t.profilLienEnvoye)
    } catch (echec) {
      signaler(echec)
    } finally {
      setEnvoiEnCours(false)
    }
  }

  async function seDeconnecter(partout: boolean) {
    await (partout ? deconnecterPartout() : deconnecter())
    onDeconnecte()
  }

  return (
    <section className="profil">
      <div className="page-entete">
        <h2>{t.monProfil}</h2>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}
      {succes !== null && <Bandeau ton="succes">{succes}</Bandeau>}

      <div className="deux-colonnes">
        <div className="pile">
          <Bloc>
            <div className="profil__identite">
              <span className="avatar avatar--lg" aria-hidden="true">
                {initiales(profil.displayName)}
              </span>
              <div>
                <p className="profil__nom">{profil.displayName}</p>
                <p className="secondaire">
                  {t.membreDepuis} {moisAnnee(profil.memberSince, langue)}
                </p>
              </div>
            </div>

            <form className="formulaire formulaire--deux" onSubmit={enregistrer} noValidate>
              <Champ id="prenom" libelle={t.prenom} valeur={prenom} autoComplete="given-name" onChange={setPrenom} />
              <Champ id="nom" libelle={t.nom} valeur={nom} autoComplete="family-name" onChange={setNom} />
              <Champ id="nomAffiche" libelle={t.nomAffiche} valeur={nomAffiche} requis onChange={setNomAffiche} />
              <Champ
                id="dateNaissance"
                libelle={t.dateNaissance}
                type="date"
                valeur={dateNaissance}
                onChange={setDateNaissance}
              />
              <Liste
                id="genre"
                libelle={t.genre}
                valeur={genre}
                options={[
                  { valeur: 'Unspecified', libelle: t.genreNonPrecise },
                  { valeur: 'Female', libelle: t.genreFemme },
                  { valeur: 'Male', libelle: t.genreHomme },
                ]}
                onChange={(valeur) => setGenre(valeur as Genre)}
              />
              <Liste
                id="langueProfil"
                libelle={t.langueProfil}
                valeur={langueProfil}
                /*
                  Les langues viennent de `LANGUES`, jamais d'une liste recopiée : c'est ce qui a
                  manqué le jour où l'anglais est arrivé — cette liste-ci ne l'aurait pas su.
                */
                options={LANGUES.map((code) => ({ valeur: code, libelle: NOMS_DE_LANGUE[code] }))}
                onChange={(valeur) => setLangueProfil(valeur as Langue)}
              />
              <Liste
                id="villeProfil"
                libelle={t.villeMaVille}
                valeur={villeProfil}
                options={[
                  { valeur: '', libelle: t.villeProfilAucune },
                  ...ville.villes
                    .filter((candidate) => candidate.status === 'Open' || candidate.code === villeProfil)
                    .map((candidate) => ({ valeur: candidate.code, libelle: libelleVille(candidate, langue) })),
                ]}
                aide={t.villeProfilAide}
                onChange={setVilleProfil}
              />
              <Zone
                id="presentation"
                libelle={t.presentation}
                valeur={presentation}
                pleineLargeur
                onChange={setPresentation}
              />
              <div className="rangee rangee--fin pleine-largeur">
                <Bouton type="submit" charge={enCours}>
                  {t.enregistrer}
                </Bouton>
              </div>
            </form>
          </Bloc>

          <Bloc titre={t.mesRoles}>
            <ul className="liste-lignes">
              {ROLES_AUTO.map((role) => {
                const actif = profil.activeRoles.includes(role)

                return (
                  <li key={role}>
                    <span className="liste-lignes__corps">
                      <span className="liste-lignes__titre">{role === 'Seeker' ? t.roleSeeker : t.roleOwner}</span>
                      <span className="liste-lignes__meta">
                        {actif ? t.profilRoleActif : role === 'Seeker' ? t.profilRoleSeekerAide : t.profilRoleOwnerAide}
                      </span>
                    </span>
                    <Bouton
                      apparence={actif ? 'discret' : 'secondaire'}
                      taille="sm"
                      onClick={() => void basculerRole(role, actif)}
                    >
                      {actif ? t.desactiver : t.activer}
                    </Bouton>
                  </li>
                )
              })}

              {/* Affichés sans commande : `Tenant` découle d'un bail signé, `Agency` est une
                  affirmation professionnelle accordée par un administrateur. Un bouton ici en
                  ferait des cases à cocher, c'est-à-dire des signaux sans valeur. */}
              <li>
                <span className="liste-lignes__corps">
                  <span className="liste-lignes__titre">{t.roleTenant}</span>
                  <span className="liste-lignes__meta">{t.roleTenantAide}</span>
                </span>
                {profil.activeRoles.includes('Tenant') ? (
                  <Puce ton="succes">{t.profilRoleActif}</Puce>
                ) : (
                  <Puce>{t.profilRoleInactif}</Puce>
                )}
              </li>
              <li>
                <span className="liste-lignes__corps">
                  <span className="liste-lignes__titre">{t.roleAgency}</span>
                  <span className="liste-lignes__meta">{t.roleAgencyAide}</span>
                </span>
                {profil.activeRoles.includes('Agency') ? (
                  <Puce ton="succes">{t.profilRoleActif}</Puce>
                ) : (
                  <Puce>{t.profilRoleNonAccorde}</Puce>
                )}
              </li>
            </ul>
          </Bloc>
        </div>

        <div className="pile">
          <Bloc titre={t.niveauConfiance}>
            <div className="jauge profil__jauge">
              <div className="jauge__ligne">
                <span className="fort">
                  {profil.trustLevel} — {libelleNiveau[profil.trustLevel]}
                </span>
                <span>
                  {nombre(rang, langue)} / {nombre(4, langue)}
                </span>
              </div>
              <div className="jauge__piste">
                <div className="jauge__valeur" style={{ inlineSize: `${rang * 25}%` }} />
              </div>
            </div>
            <ul className="liste-lignes">
              <Critere
                fait={profil.profileComplete}
                titre={t.profilCritereProfil}
                aide={profil.profileComplete ? undefined : t.profilIncomplet}
              />
              <Critere
                fait={rang >= 2}
                titre={t.profilCriterePhoto}
                aide={rang >= 2 ? undefined : t.profilCriterePhotoAide}
              />
              <Critere
                fait={contactVerifie}
                titre={t.profilCritereContact}
                aide={contactVerifie ? undefined : t.profilCritereContactAide}
              />
              <Critere fait={rang >= 3} titre={t.profilCritereVerifiee} aide={t.profilCritereVerifieeAide} />
            </ul>
          </Bloc>

          <Bloc titre={t.profilSecurite}>
            <div className="pile pile--serree">
              <Bouton
                apparence="secondaire"
                bloc
                charge={envoiEnCours}
                desactive={contact === null}
                onClick={() => void changerMotDePasse()}
              >
                {t.profilChangerMotDePasse}
              </Bouton>
              <Bouton apparence="discret" bloc onClick={() => void seDeconnecter(true)}>
                {t.deconnecterPartout}
              </Bouton>
              <LienBouton to="/confidentialite" apparence="discret" bloc>
                {t.confidentialite}
              </LienBouton>
            </div>
          </Bloc>

          {/*
            La réputation de LOCATAIRE / COLOCATAIRE : celle qu'un foyer ou un bailleur regarde.
            Celle de bailleur s'affiche sur la fiche d'annonce, où elle a un sens (conception L8,
            §1.9) — les fusionner n'en aurait aucun : on peut être un excellent colocataire et un
            propriétaire injoignable.
          */}
          <BlocReputation cible={{ nature: 'utilisateur', id: profil.id }} langue={langue} />

          {/* TODO(D22) : tant que la vérification est court-circuitée, le contact n'est jamais
              confirmé. Le dire explicitement plutôt que d'afficher un badge « vérifié » qui serait
              faux — un identifiant ne prouve rien dans cette fenêtre. */}
          {!contactVerifie && (
            <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
              {t.contactNonVerifie}
            </Bandeau>
          )}

          <div>
            <Bouton apparence="discret" onClick={() => void seDeconnecter(false)}>
              <Icone nom="sortie" taille="sm" /> {t.seDeconnecter}
            </Bouton>
          </div>
        </div>
      </div>
    </section>
  )
}

/** Un critère du niveau de confiance : une coche s'il est acquis, une horloge sinon. */
function Critere({ fait, titre, aide }: { fait: boolean; titre: string; aide?: string }) {
  return (
    <li>
      <span className={fait ? 'profil__critere est-fait' : 'profil__critere est-a-faire'}>
        <Icone nom={fait ? 'coche' : 'horloge'} />
      </span>
      <span className="liste-lignes__corps">
        <span className="liste-lignes__titre">{titre}</span>
        {aide !== undefined && <span className="liste-lignes__meta">{aide}</span>}
      </span>
    </li>
  )
}
